import { PlayIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { JsonValue } from 'type-fest';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { extractSessionIdFromUrl } from '@/util/sessionIdExtractor';

interface InsightTestSectionProps {
  testSessionId: string;
  setTestSessionId: (value: string) => void;
  testResult: JsonValue | null;
  testError: string | null;
  testStatus: 'idle' | 'pending' | 'success' | 'error';
  hasAttemptedTest: boolean;
  onTest: () => void;
  readonly?: boolean;
}

/**
 * Component for testing insights with conversation sessions
 * @param testSessionId - Current session ID input value
 * @param setTestSessionId - Function to update session ID
 * @param testResult - Result from the test API call
 * @param testError - Error message from failed test
 * @param testStatus - Current status of the test operation
 * @param hasAttemptedTest - Whether a test has been attempted
 * @param onTest - Function to execute the test
 * @param readonly - Whether the component should be read-only
 */
export const InsightTestSection = ({
  testSessionId,
  setTestSessionId,
  testResult,
  testError,
  testStatus,
  hasAttemptedTest,
  onTest,
}: InsightTestSectionProps) => {
  const { t } = useTranslation();

  return (
    <div className="pt-4">
      <div className="flex items-center gap-2">
        <Input
          placeholder={t('insights.ask_question_placeholder')}
          className="flex-1 rounded-md bg-background"
          value={testSessionId}
          onChange={(e) => {
            setTestSessionId(e.target.value);
          }}
          onBlur={(e) => {
            const sessionId = extractSessionIdFromUrl(e.target.value);
            setTestSessionId(sessionId);
          }}
          onPaste={(e) => {
            const pastedText = e.clipboardData.getData('text');
            const sessionId = extractSessionIdFromUrl(pastedText);
            if (sessionId !== pastedText) {
              e.preventDefault();
              setTestSessionId(sessionId);
            }
          }}
          disabled={testStatus === 'pending'}
        />
        <Button
          type="button"
          variant="outline-neutral"
          size="icon-md"
          onClick={onTest}
          disabled={!testSessionId.trim()}
          isLoading={testStatus === 'pending'}
        >
          <PlayIcon size={16} />
        </Button>
      </div>
      <p className="mt-1 text-sm text-foreground-muted">
        {t('insights.test_with_conversation_description')}
      </p>
      <div className="mt-4">
        {testResult && <p className="mb-2 text-sm text-foreground-muted">{t('common.results')}</p>}
        {testError && (
          <div className="rounded border border-red-200 bg-red-50 p-2">
            <p className="text-sm text-warning">{testError}</p>
          </div>
        )}
        {testResult !== null && !testError && (
          <div className="rounded border border-green-200 bg-green-50 p-2">
            <p className="text-sm">
              {typeof testResult === 'string' ? testResult : JSON.stringify(testResult, null, 2)}
            </p>
          </div>
        )}
        {testResult === null && !testError && testStatus !== 'pending' && hasAttemptedTest && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2">
            <p className="text-sm">{t('insights.no_test_results_yet')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
